import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI, hrAPI, normalizeList, normalizeError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Search, Edit, Trash2, Users } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { COUNTRIES, STATES_BY_COUNTRY } from '../lib/locationData';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { PageLoading, PageError, PageEmpty } from '../components/ui/page-guards';
import { DataPagination } from '../components/ui/data-pagination';

export default function Clients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: '',
    industry: '',
    website: '',
    notes: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
  });

  useEffect(() => {
    loadClients();
  }, [search, currentPage, pageSize]);

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    try {
      const res = await hrAPI.getTeamMembers();
      const members = normalizeList(res.data, 'users');
      // Also include current user if not already in list
      if (user && !members.find(m => m.id === user.id)) {
        members.push({ id: user.id, name: user.name, email: user.email, role: 'Admin' });
      }
      setTeamMembers(members);
    } catch (err) {
      console.error('Failed to load team members:', err);
      // Fallback: at least include current user
      if (user) {
        setTeamMembers([{ id: user.id, name: user.name, email: user.email, role: 'Admin' }]);
      }
    }
  };

  const getCreatorName = (createdBy) => {
    const member = teamMembers.find(m => m.id === createdBy);
    return member ? member.name : 'Unknown';
  };

  // Check if current user can update a resource
  const canUpdate = (resource) => {
    if (!user) return false;
    // Admins can update all
    if (!user.module_permissions) return true;
    // Team users need explicit update permission
    return hasPermission('client', 'client', 'update');
  };

  // Check if current user can delete a resource
  const canDelete = (resource) => {
    if (!user) return false;
    // Admins can delete all
    if (!user.module_permissions) return true;
    // Team users need explicit delete permission
    return hasPermission('client', 'client', 'delete');
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const skip = (currentPage - 1) * pageSize;
      const params = { search, limit: pageSize, skip };
      
      // Fetch clients and count in parallel
      const [clientsRes, countRes] = await Promise.all([
        clientsAPI.getAll(params),
        clientsAPI.getCount({ search })
      ]);
      
      setClients(normalizeList(clientsRes.data, 'clients'));
      setTotalCount(countRes.data?.count || 0);
    } catch (err) {
      console.error('Failed to load clients:', err);
      const errorMsg = normalizeError(err, 'Failed to load clients');
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting) return;
    setSubmitting(true);
    
    try {
      if (editingClient) {
        await clientsAPI.update(editingClient.id, formData);
        toast.success('Client updated successfully');
      } else {
        await clientsAPI.create(formData);
        toast.success('Client created successfully');
      }
      setSheetOpen(false);
      resetForm();
      loadClients();
    } catch (err) {
      const errorMsg = normalizeError(err, 'Failed to save client');
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      company_name: client.company_name || '',
      industry: client.industry || '',
      website: client.website || '',
      notes: client.notes || '',
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      zip_code: client.zip_code || '',
      country: client.country || '',
    });
    setSheetOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await clientsAPI.delete(id);
        toast.success('Client deleted successfully');
        loadClients();
      } catch (err) {
        const errorMsg = normalizeError(err, 'Failed to delete client');
        toast.error(errorMsg);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company_name: '',
      industry: '',
      website: '',
      notes: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
    });
    setEditingClient(null);
  };

  // Page state guards
  if (loading && clients.length === 0) {
    return <PageLoading variant="cards" message="Loading clients..." />;
  }

  if (error && clients.length === 0) {
    return <PageError error={error} onRetry={loadClients} backPath="/" />;
  }

  return (
    <div className="space-y-4" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Clients</h1>
          <p className="text-sm text-muted-foreground">Manage your client relationships</p>
        </div>
        {hasPermission('client', 'client', 'create') && (
          <Sheet open={sheetOpen} onOpenChange={(open) => { setSheetOpen(open); if (!open) resetForm(); }}>
            <SheetTrigger asChild>
              <Button data-testid="create-client-button">
                <Plus className="mr-2 h-4 w-4" />
                New Client
              </Button>
            </SheetTrigger>
          <SheetContent className="overflow-y-auto" data-testid="client-sheet">
            <SheetHeader>
              <SheetTitle>{editingClient ? 'Edit Client' : 'Create New Client'}</SheetTitle>
            </SheetHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground">Basic Information</h3>
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="client-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="client-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      placeholder="+1 (555) 000-0000"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      data-testid="client-phone-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company</Label>
                    <Input
                      id="company_name"
                      placeholder="Acme Corporation"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      data-testid="client-company-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry</Label>
                    <Input
                      id="industry"
                      placeholder="Technology"
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      data-testid="client-industry-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    placeholder="https://www.example.com"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    data-testid="client-website-input"
                  />
                </div>
              </div>

              {/* Official Permanent Address */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-sm font-semibold text-muted-foreground">Official Permanent Address</h3>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Textarea
                    id="address"
                    placeholder="123 Main Street, Suite 100"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={2}
                    data-testid="client-address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Select
                      value={formData.country}
                      onValueChange={(value) => setFormData({ ...formData, country: value, state: '' })}
                    >
                      <SelectTrigger data-testid="client-country-input">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State/Province</Label>
                    <Select
                      value={formData.state}
                      onValueChange={(value) => setFormData({ ...formData, state: value })}
                      disabled={!formData.country}
                    >
                      <SelectTrigger data-testid="client-state-input">
                        <SelectValue placeholder={formData.country ? "Select state" : "Select country first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {(STATES_BY_COUNTRY[formData.country] || []).map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      placeholder="New York"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      data-testid="client-city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip_code">Zip Code</Label>
                    <Input
                      id="zip_code"
                      placeholder="10001"
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                      data-testid="client-zipcode-input"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Additional notes about this client..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    data-testid="client-notes-input"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" disabled={submitting} data-testid="client-submit-button">
                  {submitting ? 'Saving...' : (editingClient ? 'Update Client' : 'Create Client')}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setSheetOpen(false); resetForm(); }}>
                  Cancel
                </Button>
              </div>
            </form>
          </SheetContent>
        </Sheet>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search clients..."
            className="pl-8"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1); // Reset to first page when searching
            }}
            data-testid="clients-search-input"
          />
        </div>
      </div>

      {/* Loading State (for subsequent loads with data) */}
      {loading && clients.length > 0 && (
        <div className="text-center py-4 text-muted-foreground">Loading...</div>
      )}

      {/* Empty State - No clients exist */}
      {!loading && clients.length === 0 && !search && (
        <PageEmpty
          title="No clients yet"
          message="Get started by adding your first client. Clients help you track your business relationships."
          icon={Users}
          actionLabel={hasPermission('client', 'client', 'create') ? "Add Client" : undefined}
          onAction={hasPermission('client', 'client', 'create') ? () => setSheetOpen(true) : undefined}
        />
      )}

      {/* Search Empty State - No results for search */}
      {!loading && clients.length === 0 && search && (
        <PageEmpty
          title="No clients found"
          message={`No clients match "${search}". Try a different search term.`}
          icon={Search}
          actionLabel="Clear Search"
          onAction={() => setSearch('')}
        />
      )}

      {/* Data Table */}
      {!loading && clients.length > 0 && (
        <div className="rounded-md border" data-testid="clients-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]">S.No</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Industry</TableHead>
                <TableHead>Created By</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((client, index) => (
                <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                  <TableCell className="text-muted-foreground">{(currentPage - 1) * pageSize + index + 1}</TableCell>
                  <TableCell className="font-medium">
                    <button
                      onClick={() => navigate(`/clients/${client.id}`)}
                      className="text-primary hover:underline focus:outline-none"
                      data-testid={`client-name-${client.id}`}
                    >
                      {client.name}
                    </button>
                  </TableCell>
                  <TableCell>{client.company_name || '-'}</TableCell>
                  <TableCell>{client.email || '-'}</TableCell>
                  <TableCell>{client.phone || '-'}</TableCell>
                  <TableCell>{client.industry || '-'}</TableCell>
                  <TableCell>{getCreatorName(client.created_by)}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {canUpdate(client) && (
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(client)} data-testid={`edit-client-${client.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete(client) && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(client.id)} data-testid={`delete-client-${client.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          <DataPagination
            currentPage={currentPage}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        </div>
      )}
    </div>
  );
}
